import { CheckCircle } from 'components/icons';
import { RecordLink } from 'components/record-chip/RecordLink';
import { formatNumber } from 'components/util/HoursDisplay';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import { CaseClosingBlocker, CaseClosingCheckDto, CaseClosingConditionDto } from 'types/caseClosing';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';
import { invoiceUrl, uninvoicedExpensesUrl, uninvoicedTimecardsUrl } from './closingLinks';
import { NEW_TAB_LINK_CLASS, NewTabLink, WarningRow } from './WarningRow';

const MET_MESSAGE_IDS: Record<CaseClosingBlocker, string> = {
    [CaseClosingBlocker.UNINVOICED_HOURS]: 'case-closing-modal.conditions.met.uninvoiced-hours',
    [CaseClosingBlocker.UNINVOICED_EXPENSES]: 'case-closing-modal.conditions.met.uninvoiced-expenses',
    [CaseClosingBlocker.UNFINISHED_TASKS]: 'case-closing-modal.conditions.met.unfinished-tasks',
    [CaseClosingBlocker.UNSETTLED_INVOICES]: 'case-closing-modal.conditions.met.unsettled-invoices',
    [CaseClosingBlocker.UNUSED_PREPAYMENTS]: 'case-closing-modal.conditions.met.unused-prepayments',
};

/** Unfinished tasks are reported by the tasks line under the conditions, met or not. */
function isListedHere(condition: CaseClosingConditionDto): boolean {
    return condition.blocker !== CaseClosingBlocker.UNFINISHED_TASKS;
}

export function ClosingConditionsSection({ check }: { check: CaseClosingCheckDto }) {
    return (
        <section className="flex flex-col gap-2">
            <h3 className="text-h6 m-0 font-semibold">
                <FormattedMessage id="case-closing-modal.conditions.title" />
            </h3>
            {check.conditions
                .filter(isListedHere)
                .map((condition) =>
                    condition.isMet ? (
                        <MetCondition key={condition.blocker} blocker={condition.blocker} />
                    ) : (
                        <UnmetCondition key={condition.blocker} blocker={condition.blocker} check={check} />
                    )
                )}
        </section>
    );
}

function MetCondition({ blocker }: { blocker: CaseClosingBlocker }) {
    return (
        <div className="text-body2 flex items-center gap-2 px-4 py-1">
            <CheckCircle className="text-success size-4" />
            <FormattedMessage id={MET_MESSAGE_IDS[blocker]} />
        </div>
    );
}

function UnmetCondition({ blocker, check }: { blocker: CaseClosingBlocker; check: CaseClosingCheckDto }) {
    switch (blocker) {
        case CaseClosingBlocker.UNINVOICED_HOURS:
            return <UninvoicedHoursRow check={check} />;
        case CaseClosingBlocker.UNINVOICED_EXPENSES:
            return <UninvoicedExpensesRow check={check} />;
        case CaseClosingBlocker.UNSETTLED_INVOICES:
            return <UnsettledInvoiceRows check={check} />;
        case CaseClosingBlocker.UNUSED_PREPAYMENTS:
            return <UnusedPrepaymentRows check={check} />;
        default:
            return null;
    }
}

function UninvoicedHoursRow({ check }: { check: CaseClosingCheckDto }) {
    const { displayDecimalScale } = useTimecardPrecision();
    const hours = check.uninvoicedHours;
    return (
        <WarningRow>
            <span>
                <FormattedMessage
                    id="case-closing-modal.conditions.uninvoiced-hours"
                    values={{
                        hours: formatNumber(hours?.hours ?? 0, displayDecimalScale),
                        count: hours?.timecardCount ?? 0,
                    }}
                />
            </span>
            <NewTabLink href={uninvoicedTimecardsUrl(check.matterId)}>
                <FormattedMessage id="case-closing-modal.conditions.open-uninvoiced-timecards" />
            </NewTabLink>
        </WarningRow>
    );
}

function UninvoicedExpensesRow({ check }: { check: CaseClosingCheckDto }) {
    const expenses = check.uninvoicedExpenses;
    const amounts = (expenses?.amounts ?? []).map((amount, index) => (
        <Fragment key={amount.currencyTypeCode}>
            {index > 0 ? ', ' : null}
            <MoneyDisplay amount={amount.amount} currency={amount.currencyTypeCode} />
        </Fragment>
    ));
    return (
        <WarningRow>
            <span>
                <FormattedMessage
                    id="case-closing-modal.conditions.uninvoiced-expenses"
                    values={{ count: expenses?.expenseCount ?? 0, amount: amounts }}
                />
            </span>
            <NewTabLink href={uninvoicedExpensesUrl(check.matterId)}>
                <FormattedMessage id="case-closing-modal.conditions.open-uninvoiced-expenses" />
            </NewTabLink>
        </WarningRow>
    );
}

/** Invoices the user may not read come without details, so they are counted on one row of their own. */
function UnsettledInvoiceRows({ check }: { check: CaseClosingCheckDto }) {
    const noAccessCount = check.unsettledInvoices.filter((invoice) => !invoice.isAccessible).length;
    return (
        <>
            {check.unsettledInvoices.map((invoice) =>
                invoice.isAccessible ? (
                    <WarningRow key={invoice.invoiceId}>
                        <span>
                            <FormattedMessage
                                id={
                                    invoice.paymentState === 'PARTLY_PAID'
                                        ? 'case-closing-modal.conditions.unsettled-invoice-partly-paid'
                                        : 'case-closing-modal.conditions.unsettled-invoice-unpaid'
                                }
                                values={{
                                    invoice: (
                                        <InvoiceLink key="invoice" invoiceId={invoice.invoiceId}>
                                            {invoice.invoiceNumber}
                                        </InvoiceLink>
                                    ),
                                    amount: (
                                        <MoneyDisplay
                                            key="amount"
                                            amount={invoice.outstandingAmount}
                                            currency={invoice.currencyTypeCode}
                                        />
                                    ),
                                }}
                            />
                        </span>
                    </WarningRow>
                ) : null
            )}
            {noAccessCount > 0 ? (
                <WarningRow>
                    <span>
                        <FormattedMessage
                            id="case-closing-modal.conditions.unsettled-invoices-no-access"
                            values={{ count: noAccessCount }}
                        />
                    </span>
                </WarningRow>
            ) : null}
        </>
    );
}

function UnusedPrepaymentRows({ check }: { check: CaseClosingCheckDto }) {
    const noAccessCount = check.unusedPrepayments.filter((prepayment) => !prepayment.isAccessible).length;
    return (
        <>
            {check.unusedPrepayments.map((prepayment) =>
                prepayment.isAccessible ? (
                    <WarningRow key={prepayment.invoiceId}>
                        <span>
                            <FormattedMessage
                                id="case-closing-modal.conditions.unused-prepayment"
                                values={{
                                    invoice: (
                                        <InvoiceLink key="invoice" invoiceId={prepayment.invoiceId}>
                                            {prepayment.invoiceNumber}
                                        </InvoiceLink>
                                    ),
                                    amount: (
                                        <MoneyDisplay
                                            key="amount"
                                            amount={prepayment.remainingAmount}
                                            currency={prepayment.currencyTypeCode}
                                        />
                                    ),
                                }}
                            />
                        </span>
                    </WarningRow>
                ) : null
            )}
            {noAccessCount > 0 ? (
                <WarningRow>
                    <span>
                        <FormattedMessage
                            id="case-closing-modal.conditions.unused-prepayments-no-access"
                            values={{ count: noAccessCount }}
                        />
                    </span>
                </WarningRow>
            ) : null}
        </>
    );
}

function InvoiceLink({ invoiceId, children }: { invoiceId: number; children: string }) {
    return (
        <RecordLink
            anchor
            to={invoiceUrl(invoiceId)}
            label={children}
            target="_blank"
            rel="noopener noreferrer"
            className={NEW_TAB_LINK_CLASS}
        />
    );
}
